// TemplateDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Plus } from 'lucide-react';

/**
 * Dialog for selecting an agent template to onboard a new AI agent
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Array} props.templates - List of available agent templates
 * @param {function} props.onSelectTemplate - Callback when a template is selected (receives template index)
 */
export const TemplateDialog = ({
  open,
  onOpenChange,
  templates = [],
  onSelectTemplate
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="onboard-agent-btn">
          <Plus className="h-4 w-4 mr-2" />
          Onboard Agent
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Onboard AI Agent</DialogTitle>
          <DialogDescription>Select a role template to create a new AI agent</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {templates.map((template, idx) => (
            <Card 
              key={idx} 
              className="cursor-pointer hover:shadow-md hover:border-violet-300 transition-all"
              onClick={() => onSelectTemplate(idx)}
              data-testid={`template-card-${idx}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Avatar className="h-12 w-12 bg-gradient-to-br from-violet-100 to-purple-100">
                    <AvatarFallback className="bg-transparent text-violet-700 font-bold">
                      {template.name[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h4 className="font-semibold">{template.name}</h4>
                    <p className="text-sm text-muted-foreground">{template.role_title}</p>
                    <Badge variant="outline" className="mt-1 text-xs">{template.department}</Badge>
                  </div>
                </div>
                <p className="text-sm mt-3 text-muted-foreground line-clamp-2">{template.mission}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {template.automates_well?.slice(0, 3).map((skill, i) => (
                    <Badge key={i} className="bg-violet-50 text-violet-700 text-xs">{skill}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateDialog;
